/**
 * Wisdom Math Figure Component
 * 
 * Displays mathematical visualizations for each wisdom entry
 * Research-based mathematical formulas and geometric patterns
 */

import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

interface WisdomMathFigureProps {
  wisdomId: string;
  width?: number;
  height?: number;
  color?: string;
}

const WisdomMathFigure: React.FC<WisdomMathFigureProps> = ({
  wisdomId,
  width = 300,
  height = 300,
  color = '#6366f1'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    // Scale line width with canvas size
    ctx.lineWidth = Math.max(2, Math.min(width, height) / 150);

    const centerX = width / 2;
    const centerY = height / 2;
    // Use more of the available space - 80% of the smaller dimension
    const radius = Math.min(width, height) * 0.4;

    // Draw based on wisdom ID
    switch (wisdomId) {
      case '1': // The Sacred Meaning of Fr00t
        drawFr00tCalculation(ctx, centerX, centerY, radius);
        break;
      case '2': // The Golden Ratio
        drawGoldenRatio(ctx, centerX, centerY, radius);
        break;
      case '3': // Pythagorean Numerology
        drawPythagoreanSystem(ctx, centerX, centerY, radius);
        break;
      case '5': // Mindful Presence
        drawBrainWaves(ctx, centerX, centerY, radius);
        break;
      case '6': // Infinite Void
        drawVoid(ctx, centerX, centerY, radius);
        break;
      case '7': // Flower of Life
        drawFlowerOfLife(ctx, centerX, centerY, radius);
        break;
      case '8': // 432 Hz Tuning
        drawFrequency432(ctx, centerX, centerY, radius);
        break;
      case '9': // Vipassana
        drawVipassana(ctx, centerX, centerY, radius);
        break;
      case '10': // Sacred Partnership (T=2)
        drawVesicaPiscis(ctx, centerX, centerY, radius);
        break;
      case '11': // Master Number 11
        drawMasterNumber11(ctx, centerX, centerY, radius);
        break;
      case '13': // Metatron's Cube
        drawMetatronsCube(ctx, centerX, centerY, radius);
        break;
      case '14': // Fibonacci Sequence
        drawFibonacci(ctx, centerX, centerY, radius);
        break;
      case '15': // Schumann Resonance
        drawSchumannResonance(ctx, centerX, centerY, radius);
        break;
      case '16': // Eight-Fold Path
        drawEightFoldPath(ctx, centerX, centerY, radius);
        break;
      case '17': // Torus
        drawTorus(ctx, centerX, centerY, radius);
        break;
      case '18': // Cymatics
        drawCymatics(ctx, centerX, centerY, radius);
        break;
      case '19': // Sri Yantra
        drawSriYantra(ctx, centerX, centerY, radius);
        break;
      case '20': // Heart Coherence
        drawHeartCoherence(ctx, centerX, centerY, radius);
        break;
      case '21': // Merkaba
        drawMerkaba(ctx, centerX, centerY, radius);
        break;
      case '22': // As Above, So Below
        drawHermeticPrinciple(ctx, centerX, centerY, radius);
        break;
      case '23': // Breath Ratios
        drawBreathRatios(ctx, centerX, centerY, radius);
        break;
      case '24': // Vesica Piscis
        drawVesicaPiscis(ctx, centerX, centerY, radius);
        break;
      case '25': // Law of Vibration
        drawVibration(ctx, centerX, centerY, radius);
        break;
      case '26': // Tao Te Ching
        drawYinYang(ctx, centerX, centerY, radius);
        break;
      case '27': // I Ching
        drawIChingHexagram(ctx, centerX, centerY, radius);
        break;
      case '28': // Tree of Life
        drawTreeOfLife(ctx, centerX, centerY, radius);
        break;
      case '29': // Seven Chakras
        drawChakras(ctx, centerX, centerY, radius);
        break;
      case '30': // Quantum Consciousness
        drawDoubleSlit(ctx, centerX, centerY, radius);
        break;
      case '31': // Zen Buddhism
        drawEnso(ctx, centerX, centerY, radius);
        break;
      case '32': // Stoicism
        drawStoicism(ctx, centerX, centerY, radius);
        break;
      case '33': // Alchemy
        drawAlchemy(ctx, centerX, centerY, radius);
        break;
      case '34': // Sacred Architecture
        drawSacredArchitecture(ctx, centerX, centerY, radius);
        break;
      case '35': // Mayan Calendar
        drawMayanCalendar(ctx, centerX, centerY, radius);
        break;
      case '36': // Advaita Vedanta
        drawAdvaitaVedanta(ctx, centerX, centerY, radius);
        break;
      case '37': // Lucid Dreaming
        drawLucidDreaming(ctx, centerX, centerY, radius);
        break;
      case '38': // Shadow Work
        drawShadowWork(ctx, centerX, centerY, radius);
        break;
      case '39': // Sacred Numbers
        drawSacredNumbers(ctx, centerX, centerY, radius);
        break;
      case '40': // Music Theory
        drawHarmonicSeries(ctx, centerX, centerY, radius);
        break;
      case '41': // Bhagavad Gita
        drawBhagavadGita(ctx, centerX, centerY, radius);
        break;
      case '42': // Four Noble Truths
        drawFourNobleTruths(ctx, centerX, centerY, radius);
        break;
      case '43': // Hermetic Principles
        drawHermeticPrinciples(ctx, centerX, centerY, radius);
        break;
      case '44': // Indigenous Wisdom
        drawIndigenousWisdom(ctx, centerX, centerY, radius);
        break;
      case '45': // Enneagram
        drawEnneagram(ctx, centerX, centerY, radius);
        break;
      case '46': // Platonic Space (Khôra/Receptacle)
        drawPlatonicSpace(ctx, centerX, centerY, radius);
        break;
      default:
        drawDefault(ctx, centerX, centerY, radius);
    }
  }, [wisdomId, width, height, color]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          border: `2px solid ${color}40`,
          borderRadius: '8px',
          background: 'rgba(15, 23, 42, 0.8)',
          maxWidth: '100%'
        }}
      />
    </motion.div>
  );
};

// ============================================================================
// DRAWING FUNCTIONS - Mathematical Visualizations
// ============================================================================

/**
 * Helper function to calculate font size based on radius
 */
function getFontSize(radius: number, scale: number = 0.12): number {
  return Math.max(12, radius * scale);
}

/**
 * Helper function to calculate line width based on radius
 */
function getLineWidth(radius: number, scale: number = 0.015): number {
  return Math.max(2, radius * scale);
}

/**
 * 1. Fr00t Calculation: F(6) + R(9) + 0(0) + 0(0) + T(2) = 17 → 1+7 = 8
 */
function drawFr00tCalculation(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = Math.max(18, radius * 0.15);
  const largeFontSize = Math.max(72, radius * 0.8);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#6366f1';
  
  const y = centerY - radius * 0.8;
  const lineHeight = fontSize * 1.4;
  ctx.fillText('F(6) + R(9) + 0(0) + 0(0) + T(2)', centerX, y);
  ctx.fillText('= 17', centerX, y + lineHeight);
  ctx.fillText('1 + 7 = 8', centerX, y + lineHeight * 2);
  ctx.fillText('Power & Abundance', centerX, y + lineHeight * 3);
  
  // Draw number 8 in center
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = Math.max(4, radius * 0.02);
  ctx.beginPath();
  ctx.arc(centerX, centerY + radius * 0.5, radius * 0.4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.font = `${largeFontSize}px monospace`;
  ctx.fillText('8', centerX, centerY + radius * 0.5 + largeFontSize * 0.35);
}

/**
 * 2. Golden Ratio: φ = (1 + √5)/2 ≈ 1.618...
 */
function drawGoldenRatio(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const phi = (1 + Math.sqrt(5)) / 2;
  const fontSize = Math.max(16, radius * 0.12);
  
  // Draw formula
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('φ = (1 + √5) / 2', centerX, centerY - radius * 0.9);
  ctx.fillText(`≈ ${phi.toFixed(6)}`, centerX, centerY - radius * 0.7);
  
  // Draw golden rectangle and spiral - use more space
  const rectWidth = radius * 1.8;
  const rectHeight = rectWidth / phi;
  
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = Math.max(2, radius * 0.015);
  ctx.strokeRect(centerX - rectWidth / 2, centerY - rectHeight / 2, rectWidth, rectHeight);
  
  // Draw golden spiral (simplified)
  ctx.beginPath();
  let a = radius * 0.15;
  const b = Math.log(phi) / (Math.PI / 2);
  for (let angle = 0; angle < 3 * Math.PI; angle += 0.1) {
    const r = a * Math.exp(b * angle);
    const x = centerX - rectWidth / 2 + r * Math.cos(angle);
    const y = centerY - rectHeight / 2 + r * Math.sin(angle);
    if (angle === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
}

/**
 * 3. Pythagorean Numerology: Letter-to-number mapping
 */
function drawPythagoreanSystem(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = getFontSize(radius, 0.14);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  
  // Show mapping: A=1, B=2...I=9, J=1, K=2...R=9
  const examples = [
    'A=1, B=2, C=3',
    'I=9, J=1, K=2',
    'R=9, S=1, T=2',
    'Z=8'
  ];
  
  const lineHeight = fontSize * 1.5;
  examples.forEach((text, i) => {
    ctx.fillText(text, centerX, centerY - radius * 0.8 + i * lineHeight);
  });
  
  // Draw circular arrangement - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.01);
  const circleRadius = radius * 0.08;
  for (let i = 0; i < 9; i++) {
    const angle = (i * Math.PI * 2) / 9;
    const x = centerX + radius * 0.7 * Math.cos(angle);
    const y = centerY + radius * 0.7 * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(x, y, circleRadius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.font = `${fontSize * 0.9}px monospace`;
    ctx.fillText((i + 1).toString(), x, y + fontSize * 0.3);
  }
}

/**
 * 5. Brain Waves: Alpha (7-13 Hz), Theta (4-7 Hz)
 */
function drawBrainWaves(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  const fontSize = getFontSize(radius, 0.1);
  
  // Draw sine waves for different brain states - use more space
  const frequencies = [
    { freq: 10, label: 'Alpha (10 Hz)', y: centerY - radius * 0.6 },
    { freq: 5.5, label: 'Theta (5.5 Hz)', y: centerY },
    { freq: 2, label: 'Delta (2 Hz)', y: centerY + radius * 0.6 }
  ];
  
  frequencies.forEach(({ freq, label, y }) => {
    ctx.beginPath();
    const waveWidth = radius * 1.6;
    const waveAmplitude = radius * 0.15;
    for (let x = centerX - waveWidth / 2; x <= centerX + waveWidth / 2; x += 2) {
      const t = (x - (centerX - waveWidth / 2)) / waveWidth;
      const waveY = y + Math.sin(t * freq * Math.PI * 2) * waveAmplitude;
      if (x === centerX - waveWidth / 2) ctx.moveTo(x, waveY);
      else ctx.lineTo(x, waveY);
    }
    ctx.stroke();
    ctx.font = `${fontSize}px sans-serif`;
    ctx.fillText(label, centerX + waveWidth / 2 + fontSize, y);
  });
}

/**
 * 6. Void (Śūnyatā): Symbolized by 0
 */
function drawVoid(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw concentric circles representing infinite potential - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.012);
  
  for (let i = 1; i <= 6; i++) {
    const r = (radius * 0.9 * i) / 6;
    ctx.beginPath();
    ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  
  // Center point (bindu)
  const binduSize = Math.max(4, radius * 0.02);
  ctx.beginPath();
  ctx.arc(centerX, centerY, binduSize, 0, Math.PI * 2);
  ctx.fill();
  
  const fontSize = getFontSize(radius, 0.2);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('0', centerX, centerY + fontSize * 0.35);
  ctx.font = `${getFontSize(radius, 0.12)}px sans-serif`;
  ctx.fillText('Infinite Potential', centerX, centerY + radius * 0.9 + fontSize);
}

/**
 * 7. Flower of Life: 19 overlapping circles
 */
function drawFlowerOfLife(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const circleRadius = radius * 0.35; // Use more space
  ctx.lineWidth = getLineWidth(radius, 0.012);
  
  // Center circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
  ctx.stroke();
  
  // First ring (6 circles)
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI * 2) / 6;
    const x = centerX + circleRadius * 2 * Math.cos(angle);
    const y = centerY + circleRadius * 2 * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(x, y, circleRadius, 0, Math.PI * 2);
    ctx.stroke();
  }
  
  // Second ring (12 circles) - simplified
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI * 2) / 6;
    const outerRadius = circleRadius * 2 * Math.sqrt(3);
    const x = centerX + outerRadius * Math.cos(angle);
    const y = centerY + outerRadius * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(x, y, circleRadius, 0, Math.PI * 2);
    ctx.stroke();
  }
}

/**
 * 8. 432 Hz Frequency: Wave pattern
 */
function drawFrequency432(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = getFontSize(radius, 0.14);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('432 Hz', centerX, centerY - radius * 0.9);
  ctx.fillText('Natural Tuning', centerX, centerY - radius * 0.7);
  
  // Draw wave pattern - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  const waveWidth = radius * 1.6;
  const waveAmplitude = radius * 0.2;
  ctx.beginPath();
  for (let x = centerX - waveWidth / 2; x <= centerX + waveWidth / 2; x += 1) {
    const t = (x - (centerX - waveWidth / 2)) / waveWidth;
    const waveY = centerY + Math.sin(t * 432 * Math.PI * 2 / 100) * waveAmplitude;
    if (x === centerX - waveWidth / 2) ctx.moveTo(x, waveY);
    else ctx.lineTo(x, waveY);
  }
  ctx.stroke();
  
  // Show relationship to Schumann (7.83 Hz)
  ctx.font = `${getFontSize(radius, 0.1)}px sans-serif`;
  ctx.fillText('Resonates with Earth (7.83 Hz)', centerX, centerY + radius * 0.9);
}

/**
 * 9. Vipassana: Three characteristics
 */
function drawVipassana(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  
  const characteristics = [
    'Anicca (Impermanence)',
    'Dukkha (Suffering)',
    'Anatta (Non-self)'
  ];
  
  // Draw three circles representing the three characteristics - use more space
  ctx.lineWidth = getLineWidth(radius, 0.015);
  for (let i = 0; i < 3; i++) {
    const angle = (i * Math.PI * 2) / 3 - Math.PI / 2;
    const x = centerX + radius * 0.65 * Math.cos(angle);
    const y = centerY + radius * 0.65 * Math.sin(angle);
    
    ctx.strokeStyle = '#6366f1';
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.35, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.fillText(characteristics[i], x, y + radius * 0.65 + fontSize * 1.2);
  }
}

/**
 * 10. Vesica Piscis: Two overlapping circles, ratio √3:1
 */
function drawVesicaPiscis(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const circleRadius = radius * 0.75; // Use more space
  ctx.lineWidth = getLineWidth(radius, 0.015);
  
  // Two overlapping circles
  ctx.beginPath();
  ctx.arc(centerX - circleRadius / 2, centerY, circleRadius, 0, Math.PI * 2);
  ctx.stroke();
  
  ctx.beginPath();
  ctx.arc(centerX + circleRadius / 2, centerY, circleRadius, 0, Math.PI * 2);
  ctx.stroke();
  
  // Show ratio
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('Ratio: √3:1', centerX, centerY + radius * 0.9);
  ctx.fillText('≈ 1.732:1', centerX, centerY + radius * 0.9 + fontSize * 1.3);
}

/**
 * 11. Master Number 11: Gateway between realms
 */
function drawMasterNumber11(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const largeFontSize = Math.max(72, radius * 0.6);
  ctx.font = `${largeFontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('11', centerX, centerY);
  
  // Draw two parallel lines (gateway)
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  ctx.beginPath();
  ctx.moveTo(centerX - radius * 0.9, centerY - radius * 0.6);
  ctx.lineTo(centerX - radius * 0.3, centerY - radius * 0.6);
  ctx.moveTo(centerX + radius * 0.3, centerY - radius * 0.6);
  ctx.lineTo(centerX + radius * 0.9, centerY - radius * 0.6);
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.fillText('Gateway', centerX, centerY + radius * 0.9);
  ctx.fillText('Physical ↔ Spiritual', centerX, centerY + radius * 0.9 + fontSize * 1.3);
}

/**
 * 13. Metatron's Cube: 13 circles, 78 lines, contains 5 Platonic solids
 */
function drawMetatronsCube(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const circleRadius = radius * 0.3; // Use more space
  ctx.lineWidth = getLineWidth(radius, 0.012);
  const circles: { x: number; y: number }[] = [];
  
  // Center circle
  circles.push({ x: centerX, y: centerY });
  
  // Inner 6 circles
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI * 2) / 6;
    circles.push({
      x: centerX + circleRadius * 2 * Math.cos(angle),
      y: centerY + circleRadius * 2 * Math.sin(angle)
    });
  }
  
  // Outer 6 circles
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI * 2) / 6;
    const outerRadius = circleRadius * 2 * Math.sqrt(3);
    circles.push({
      x: centerX + outerRadius * Math.cos(angle),
      y: centerY + outerRadius * Math.sin(angle)
    });
  }
  
  // Draw circles
  circles.forEach(circle => {
    ctx.beginPath();
    ctx.arc(circle.x, circle.y, circleRadius, 0, Math.PI * 2);
    ctx.stroke();
  });
  
  // Draw some connecting lines (simplified - not all 78)
  ctx.strokeStyle = '#6366f140';
  ctx.lineWidth = 1;
  for (let i = 0; i < Math.min(circles.length, 7); i++) {
    for (let j = i + 1; j < Math.min(circles.length, 7); j++) {
      ctx.beginPath();
      ctx.moveTo(circles[i].x, circles[i].y);
      ctx.lineTo(circles[j].x, circles[j].y);
      ctx.stroke();
    }
  }
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.fillStyle = '#6366f1';
  ctx.textAlign = 'center';
  ctx.fillText('13 circles, 78 lines', centerX, centerY + radius * 0.9);
}

/**
 * 14. Fibonacci Sequence: 1, 1, 2, 3, 5, 8, 13, 21, 34...
 */
function drawFibonacci(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw Fibonacci squares in spiral arrangement - use more space
  const fib = [1, 1, 2, 3, 5, 8];
  let x = centerX;
  let y = centerY;
  let direction = 0; // 0=right, 1=up, 2=left, 3=down
  
  const scale = radius * 0.08; // Use more space
  
  ctx.lineWidth = getLineWidth(radius, 0.015);
  fib.forEach((num, i) => {
    const size = num * scale;
    
    ctx.strokeStyle = '#6366f1';
    ctx.strokeRect(x, y, size, size);
    
    const fontSize = getFontSize(radius, 0.08);
    ctx.font = `${fontSize}px monospace`;
    ctx.fillText(num.toString(), x + size / 2, y + size / 2 + fontSize * 0.3);
    
    // Move to next position (spiral)
    if (direction === 0) {
      x += size;
      y -= (i > 0 ? fib[i - 1] * scale : 0);
    } else if (direction === 1) {
      y -= size;
    } else if (direction === 2) {
      x -= size;
    } else {
      y += size;
    }
    
    direction = (direction + 1) % 4;
  });
  
  // Draw spiral
  ctx.strokeStyle = '#6366f180';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  ctx.beginPath();
  const phi = (1 + Math.sqrt(5)) / 2;
  let a = scale;
  const b = Math.log(phi) / (Math.PI / 2);
  for (let angle = 0; angle < 3 * Math.PI; angle += 0.1) {
    const r = a * Math.exp(b * angle);
    const spiralX = centerX + r * Math.cos(angle);
    const spiralY = centerY + r * Math.sin(angle);
    if (angle === 0) ctx.moveTo(spiralX, spiralY);
    else ctx.lineTo(spiralX, spiralY);
  }
  ctx.stroke();
}

/**
 * 15. Schumann Resonance: 7.83 Hz - Earth's heartbeat
 */
function drawSchumannResonance(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = getFontSize(radius, 0.14);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('7.83 Hz', centerX, centerY - radius * 0.9);
  ctx.fillText("Earth's Heartbeat", centerX, centerY - radius * 0.7);
  
  // Draw sine wave - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  const waveWidth = radius * 1.6;
  const waveAmplitude = radius * 0.2;
  ctx.beginPath();
  for (let x = centerX - waveWidth / 2; x <= centerX + waveWidth / 2; x += 2) {
    const t = (x - (centerX - waveWidth / 2)) / waveWidth;
    const waveY = centerY + Math.sin(t * 7.83 * Math.PI * 2) * waveAmplitude;
    if (x === centerX - waveWidth / 2) ctx.moveTo(x, waveY);
    else ctx.lineTo(x, waveY);
  }
  ctx.stroke();
  
  // Show relationship to alpha brain waves
  ctx.font = `${getFontSize(radius, 0.1)}px sans-serif`;
  ctx.fillText('Alpha brain waves: 7-13 Hz', centerX, centerY + radius * 0.9);
}

/**
 * 16. Eight-Fold Path: 8 spokes
 */
function drawEightFoldPath(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw 8 spokes
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = 2;
  for (let i = 0; i < 8; i++) {
    const angle = (i * Math.PI * 2) / 8;
    const x = centerX + radius * 0.8 * Math.cos(angle);
    const y = centerY + radius * 0.8 * Math.sin(angle);
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  
  // Center circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.2, 0, Math.PI * 2);
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Noble Eightfold Path', centerX, centerY + radius * 0.9);
}

/**
 * 17. Torus: Parametric equations
 */
function drawTorus(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw torus (simplified 2D projection) - use more space
  const R = radius * 0.75; // Major radius
  const r = radius * 0.35; // Minor radius
  
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  
  // Outer ring
  ctx.beginPath();
  ctx.arc(centerX, centerY, R + r, 0, Math.PI * 2);
  ctx.stroke();
  
  // Inner ring
  ctx.beginPath();
  ctx.arc(centerX, centerY, R - r, 0, Math.PI * 2);
  ctx.stroke();
  
  // Show formula
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('x = (R+r·cos(v))·cos(u)', centerX, centerY + radius * 0.9);
  ctx.fillText('y = (R+r·cos(v))·sin(u)', centerX, centerY + radius * 0.9 + fontSize * 1.3);
}

/**
 * 18. Cymatics: Hexagonal pattern at 528 Hz
 */
function drawCymatics(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw hexagon (pattern at 528 Hz) - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI * 2) / 6;
    const x = centerX + radius * 0.8 * Math.cos(angle);
    const y = centerY + radius * 0.8 * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.stroke();
  
  // Inner hexagon
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI * 2) / 6;
    const x = centerX + radius * 0.4 * Math.cos(angle);
    const y = centerY + radius * 0.4 * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('528 Hz → Hexagon', centerX, centerY + radius * 0.9);
}

/**
 * 19. Sri Yantra: 9 interlocking triangles
 */
function drawSriYantra(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw 4 upward triangles (Shiva)
  for (let i = 0; i < 4; i++) {
    const scale = 0.8 - i * 0.15;
    drawTriangle(ctx, centerX, centerY, radius * scale, 0);
  }
  
  // Draw 5 downward triangles (Shakti)
  for (let i = 0; i < 5; i++) {
    const scale = 0.7 - i * 0.12;
    drawTriangle(ctx, centerX, centerY, radius * scale, Math.PI);
  }
  
  // Bindu (center point)
  const binduSize = Math.max(5, radius * 0.02);
  ctx.beginPath();
  ctx.arc(centerX, centerY, binduSize, 0, Math.PI * 2);
  ctx.fill();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('9 triangles, 43 regions', centerX, centerY + radius * 0.9);
}

function drawTriangle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number
) {
  ctx.beginPath();
  for (let i = 0; i < 3; i++) {
    const angle = rotation + (i * Math.PI * 2) / 3 - Math.PI / 2;
    const px = x + size * Math.cos(angle);
    const py = y + size * Math.sin(angle);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();
}

/**
 * 20. Heart Coherence: Sine wave pattern
 */
function drawHeartCoherence(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw coherent sine wave (smooth) - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  const waveWidth = radius * 1.6;
  const waveAmplitude = radius * 0.2;
  ctx.beginPath();
  for (let x = centerX - waveWidth / 2; x <= centerX + waveWidth / 2; x += 2) {
    const t = (x - (centerX - waveWidth / 2)) / waveWidth;
    const waveY = centerY + Math.sin(t * Math.PI * 2) * waveAmplitude;
    if (x === centerX - waveWidth / 2) ctx.moveTo(x, waveY);
    else ctx.lineTo(x, waveY);
  }
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Coherent Heart Rhythm', centerX, centerY + radius * 0.9);
  ctx.fillText('Smooth sine wave', centerX, centerY + radius * 0.9 + fontSize * 1.3);
}

/**
 * 21. Merkaba: Star tetrahedron (two interlocking tetrahedra)
 */
function drawMerkaba(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw star tetrahedron (simplified 2D)
  // Upper triangle
  ctx.beginPath();
  ctx.moveTo(centerX, centerY - radius * 0.6);
  ctx.lineTo(centerX - radius * 0.5, centerY + radius * 0.3);
  ctx.lineTo(centerX + radius * 0.5, centerY + radius * 0.3);
  ctx.closePath();
  ctx.stroke();
  
  // Lower triangle (inverted)
  ctx.beginPath();
  ctx.moveTo(centerX, centerY + radius * 0.7);
  ctx.lineTo(centerX - radius * 0.6, centerY - radius * 0.3);
  ctx.lineTo(centerX + radius * 0.6, centerY - radius * 0.3);
  ctx.closePath();
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Star Tetrahedron', centerX, centerY + radius * 0.9);
  ctx.fillText('34:21 Fibonacci ratio', centerX, centerY + radius * 0.9 + fontSize * 1.3);
}

/**
 * 22. Hermetic Principle: Microcosm/Macrocosm
 */
function drawHermeticPrinciple(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw nested circles (microcosm within macrocosm) - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.012);
  
  // Macrocosm (outer)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.95, 0, Math.PI * 2);
  ctx.stroke();
  
  // Microcosm (inner)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.35, 0, Math.PI * 2);
  ctx.stroke();
  
  // Connecting lines
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI * 2) / 4;
    const x1 = centerX + radius * 0.35 * Math.cos(angle);
    const y1 = centerY + radius * 0.35 * Math.sin(angle);
    const x2 = centerX + radius * 0.95 * Math.cos(angle);
    const y2 = centerY + radius * 0.95 * Math.sin(angle);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('As Above, So Below', centerX, centerY + radius * 0.9);
}

/**
 * 23. Breath Ratios: 4-7-8, 4-4-4-4, etc.
 */
function drawBreathRatios(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = getFontSize(radius, 0.14);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  
  const ratios = [
    '4-7-8 Breathing',
    '4-4-4-4 Box',
    'Alternate Nostril'
  ];
  
  const lineHeight = fontSize * 1.5;
  ratios.forEach((ratio, i) => {
    ctx.fillText(ratio, centerX, centerY - radius * 0.8 + i * lineHeight);
  });
  
  // Draw breathing wave - use more space
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  ctx.beginPath();
  // Inhale (4), hold (7), exhale (8)
  const total = 4 + 7 + 8;
  const waveWidth = radius * 1.6;
  const waveAmplitude = radius * 0.2;
  let x = centerX - waveWidth / 2;
  let y = centerY + radius * 0.4;
  
  // Inhale
  for (let i = 0; i <= 4; i++) {
    const t = i / 4;
    const waveX = centerX - waveWidth / 2 + (waveWidth * 4 / total) * t;
    const waveY = y - Math.sin(t * Math.PI / 2) * waveAmplitude;
    if (i === 0) ctx.moveTo(waveX, waveY);
    else ctx.lineTo(waveX, waveY);
  }
  
  // Hold
  const holdX = centerX - waveWidth / 2 + (waveWidth * 4 / total);
  ctx.lineTo(holdX + (waveWidth * 7 / total), y - waveAmplitude);
  
  // Exhale
  const exhaleStartX = holdX + (waveWidth * 7 / total);
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    const waveX = exhaleStartX + (waveWidth * 8 / total) * t;
    const waveY = y - waveAmplitude + Math.sin(t * Math.PI / 2) * waveAmplitude;
    ctx.lineTo(waveX, waveY);
  }
  
  ctx.stroke();
}

/**
 * 25. Law of Vibration: Wave frequencies
 */
function drawVibration(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw multiple frequencies (everything vibrates) - use more space
  const frequencies = [1, 2, 4, 8];
  const waveWidth = radius * 1.6;
  const waveAmplitude = radius * 0.12;
  
  frequencies.forEach((freq, i) => {
    const y = centerY - radius * 0.7 + i * (radius * 0.4);
    ctx.strokeStyle = `rgba(99, 102, 241, ${1 - i * 0.2})`;
    ctx.lineWidth = getLineWidth(radius, 0.02) - i * 0.3;
    ctx.beginPath();
    for (let x = centerX - waveWidth / 2; x <= centerX + waveWidth / 2; x += 2) {
      const t = (x - (centerX - waveWidth / 2)) / waveWidth;
      const waveY = y + Math.sin(t * freq * Math.PI * 4) * waveAmplitude;
      if (x === centerX - waveWidth / 2) ctx.moveTo(x, waveY);
      else ctx.lineTo(x, waveY);
    }
    ctx.stroke();
  });
  
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Everything is Vibration', centerX, centerY + radius * 0.9);
}

/**
 * 26. Tao Te Ching: Yin-Yang symbol (Taijitu)
 * 
 * Accurate geometric construction:
 * - Outer circle of radius R
 * - Divided by S-curve into two equal halves
 * - S-curve created by two semicircles of radius R/2
 * - Yin (dark/black) on the RIGHT side
 * - Yang (light/white) on the LEFT side
 * - Yin eye (small black circle) in Yang area (top)
 * - Yang eye (small white circle) in Yin area (bottom)
 * 
 * Traditional orientation: When viewed, Yin is on the right, Yang on the left
 */
function drawYinYang(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const R = radius * 0.9; // Outer radius
  const r = R / 2; // Radius of small semicircles (half of outer radius)
  const eyeRadius = R / 6; // Eye radius (1/6 of outer radius)
  
  ctx.save();
  
  // Set colors
  const yinColor = '#000000'; // Black for Yin
  const yangColor = '#FFFFFF'; // White for Yang
  const strokeColor = '#6366f1'; // Purple for outline
  
  // Outer circle (border)
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = getLineWidth(radius, 0.02);
  ctx.beginPath();
  ctx.arc(centerX, centerY, R, 0, Math.PI * 2);
  ctx.stroke();
  
  // YIN (dark/black) - RIGHT SIDE
  // Construct the S-curve using two semicircles
  ctx.fillStyle = yinColor;
  ctx.beginPath();
  // Start from top of circle, go clockwise
  // Right semicircle (from top to bottom)
  ctx.arc(centerX, centerY, R, -Math.PI / 2, Math.PI / 2, false);
  // Top small semicircle (curves inward) - creates the S-curve
  ctx.arc(centerX, centerY - r, r, Math.PI / 2, -Math.PI / 2, true);
  // Bottom small semicircle (curves outward) - completes the S-curve
  ctx.arc(centerX, centerY + r, r, -Math.PI / 2, Math.PI / 2, false);
  ctx.closePath();
  ctx.fill();
  
  // YANG (light/white) - LEFT SIDE
  // Fill the left side with white
  ctx.fillStyle = yangColor;
  ctx.beginPath();
  // Left semicircle (from bottom to top)
  ctx.arc(centerX, centerY, R, Math.PI / 2, -Math.PI / 2, false);
  // Bottom small semicircle (curves inward)
  ctx.arc(centerX, centerY + r, r, -Math.PI / 2, Math.PI / 2, true);
  // Top small semicircle (curves outward)
  ctx.arc(centerX, centerY - r, r, Math.PI / 2, -Math.PI / 2, false);
  ctx.closePath();
  ctx.fill();
  
  // YIN EYE (small black circle in Yang/white area - TOP)
  ctx.fillStyle = yinColor;
  ctx.beginPath();
  ctx.arc(centerX, centerY - r, eyeRadius, 0, Math.PI * 2);
  ctx.fill();
  
  // YANG EYE (small white circle in Yin/black area - BOTTOM)
  ctx.fillStyle = yangColor;
  ctx.beginPath();
  ctx.arc(centerX, centerY + r, eyeRadius, 0, Math.PI * 2);
  ctx.fill();
  
  // Redraw outer border to ensure clean edges
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = getLineWidth(radius, 0.02);
  ctx.beginPath();
  ctx.arc(centerX, centerY, R, 0, Math.PI * 2);
  ctx.stroke();
  
  ctx.restore();
}

/**
 * 27. I Ching: Hexagram (6 lines)
 */
function drawIChingHexagram(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const lineSpacing = radius * 0.15;
  const lineLength = radius * 0.8;
  const startY = centerY - (lineSpacing * 2.5);
  
  // Draw 6 lines (example: Qian/Heaven - all solid)
  for (let i = 0; i < 6; i++) {
    const y = startY + i * lineSpacing;
    ctx.beginPath();
    ctx.moveTo(centerX - lineLength / 2, y);
    ctx.lineTo(centerX + lineLength / 2, y);
    ctx.stroke();
  }
  
  // Show binary representation
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('111111 = 63', centerX, centerY + radius * 0.7);
  ctx.fillText('(Qian / Heaven)', centerX, centerY + radius * 0.85);
}

/**
 * 28. Tree of Life: 10 Sephiroth with 22 paths
 */
function drawTreeOfLife(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const pulseRadius = radius * 0.9;
  const sephiroth: { x: number; y: number }[] = [];
  
  const verticalSpacing = pulseRadius * 0.35;
  const horizontalSpacing = pulseRadius * 0.45;
  const topY = centerY - pulseRadius * 0.75;
  
  // Level 1: Kether
  sephiroth.push({ x: centerX, y: topY });
  
  // Level 2: Chokmah, Binah
  sephiroth.push({ x: centerX + horizontalSpacing, y: topY + verticalSpacing });
  sephiroth.push({ x: centerX - horizontalSpacing, y: topY + verticalSpacing });
  
  // Level 3: Chesed, Geburah, Tiphareth
  sephiroth.push({ x: centerX + horizontalSpacing, y: topY + verticalSpacing * 2 });
  sephiroth.push({ x: centerX - horizontalSpacing, y: topY + verticalSpacing * 2 });
  sephiroth.push({ x: centerX, y: topY + verticalSpacing * 2 });
  
  // Level 4: Netzach, Hod, Yesod
  sephiroth.push({ x: centerX + horizontalSpacing, y: topY + verticalSpacing * 3 });
  sephiroth.push({ x: centerX - horizontalSpacing, y: topY + verticalSpacing * 3 });
  sephiroth.push({ x: centerX, y: topY + verticalSpacing * 3 });
  
  // Level 5: Malkuth
  sephiroth.push({ x: centerX, y: topY + verticalSpacing * 4 });
  
  // Draw paths (simplified - key connections)
  ctx.strokeStyle = '#6366f140';
  ctx.lineWidth = getLineWidth(radius, 0.01);
  const paths = [
    [0, 1], [0, 2], [0, 5], // From Kether
    [1, 2], [1, 3], [1, 5], // From Chokmah
    [2, 4], [2, 5], // From Binah
    [3, 4], [3, 5], [3, 6], // From Chesed
    [4, 5], [4, 7], // From Geburah
    [5, 6], [5, 7], [5, 8], // From Tiphareth
    [6, 7], [6, 8], [6, 9], // From Netzach
    [7, 8], [7, 9], // From Hod
    [8, 9] // From Yesod
  ];
  
  paths.forEach(([from, to]) => {
    ctx.beginPath();
    ctx.moveTo(sephiroth[from].x, sephiroth[from].y);
    ctx.lineTo(sephiroth[to].x, sephiroth[to].y);
    ctx.stroke();
  });
  
  // Draw sephiroth
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  sephiroth.forEach((sep, index) => {
    const sepRadius = radius * 0.05;
    ctx.beginPath();
    ctx.arc(sep.x, sep.y, sepRadius, 0, Math.PI * 2);
    ctx.stroke();
  });
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('10 Sephiroth, 22 Paths', centerX, centerY + radius * 0.9);
}

/**
 * 29. Seven Chakras: Energy centers aligned
 */
function drawChakras(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const chakras = [
    { name: 'Crown', color: '#9d4edd', y: -0.9 },
    { name: 'Third Eye', color: '#4c1d95', y: -0.6 },
    { name: 'Throat', color: '#1e40af', y: -0.3 },
    { name: 'Heart', color: '#16a34a', y: 0 },
    { name: 'Solar Plexus', color: '#eab308', y: 0.3 },
    { name: 'Sacral', color: '#f97316', y: 0.6 },
    { name: 'Root', color: '#dc2626', y: 0.9 }
  ];
  
  const chakraRadius = radius * 0.08;
  const spacing = radius * 0.15;
  
  chakras.forEach((chakra, i) => {
    const y = centerY + chakra.y * radius * 0.7;
    
    // Draw chakra circle
    ctx.strokeStyle = chakra.color;
    // Convert hex to rgba for alpha
    const r = parseInt(chakra.color.slice(1, 3), 16);
    const g = parseInt(chakra.color.slice(3, 5), 16);
    const b = parseInt(chakra.color.slice(5, 7), 16);
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.25)`;
    ctx.lineWidth = getLineWidth(radius, 0.015);
    ctx.beginPath();
    ctx.arc(centerX, y, chakraRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    
    // Draw petals (simplified - lotus petals around chakra)
    const petalCount = [1000, 2, 16, 12, 10, 6, 4][i]; // Simplified
    for (let j = 0; j < Math.min(petalCount, 12); j++) {
      const angle = (j * Math.PI * 2) / Math.min(petalCount, 12);
      const petalX = centerX + chakraRadius * 1.3 * Math.cos(angle);
      const petalY = y + chakraRadius * 1.3 * Math.sin(angle);
      ctx.beginPath();
      ctx.arc(petalX, petalY, chakraRadius * 0.3, 0, Math.PI * 2);
      ctx.stroke();
    }
  });
}

/**
 * 30. Quantum Consciousness: Double-slit experiment
 * 
 * Accurate representation of the double-slit experiment showing:
 * - Barrier with two slits
 * - Wave interference pattern (unobserved state) - multiple bright/dark bands
 * - The pattern shows constructive and destructive interference
 * 
 * Physics: When particles pass through two slits unobserved, they create
 * an interference pattern with multiple maxima and minima, indicating
 * wave behavior. The intensity follows: I(θ) = I₀ cos²(πd sin(θ)/λ)
 * where d is slit separation, λ is wavelength, θ is angle.
 */
function drawDoubleSlit(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw barrier with two slits
  const barrierWidth = radius * 0.08;
  const slitWidth = radius * 0.12;
  const slitSpacing = radius * 0.25; // Distance between slit centers
  const barrierX = centerX - radius * 0.2;
  
  // Barrier
  ctx.fillStyle = '#6366f1';
  ctx.fillRect(barrierX, centerY - radius * 0.7, barrierWidth, radius * 1.4);
  
  // Two slits (gaps in barrier) - clear rectangles
  const slit1Y = centerY - slitSpacing / 2;
  const slit2Y = centerY + slitSpacing / 2;
  ctx.clearRect(barrierX, slit1Y - slitWidth / 2, barrierWidth, slitWidth);
  ctx.clearRect(barrierX, slit2Y - slitWidth / 2, barrierWidth, slitWidth);
  
  // Draw source waves approaching barrier (left side)
  ctx.strokeStyle = 'rgba(99, 102, 241, 0.3)';
  ctx.lineWidth = getLineWidth(radius, 0.008);
  const sourceX = centerX - radius * 0.6;
  const waveCount = 3;
  for (let i = 0; i < waveCount; i++) {
    const waveY = centerY + (i - 1) * radius * 0.15;
    ctx.beginPath();
    for (let x = sourceX; x <= barrierX; x += 1) {
      const distance = barrierX - x;
      const wavePhase = (distance / radius) * Math.PI * 4;
      const y = waveY + Math.sin(wavePhase) * radius * 0.08;
      if (x === sourceX) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  
  // Accurate interference pattern (on right side of barrier)
  // This represents the intensity pattern on the detection screen
  // Physics: I(θ) = I₀ cos²(πd sin(θ)/λ) where d = slit separation, λ = wavelength
  const screenStartX = barrierX + barrierWidth + radius * 0.05;
  const screenEndX = centerX + radius * 0.7;
  const screenDistance = screenEndX - screenStartX;
  const wavelength = radius * 0.12; // Effective wavelength for visualization
  const d = slitSpacing; // Slit separation
  
  // Draw interference pattern as intensity distribution
  // Shows multiple bright (maxima) and dark (minima) bands
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  
  // Draw intensity pattern across the screen
  ctx.beginPath();
  const screenCenterY = centerY;
  for (let x = screenStartX; x <= screenEndX; x += 2) {
    const distanceFromBarrier = x - screenStartX;
    // For each vertical position on screen, calculate interference
    // Simplified: intensity varies with vertical position y
    const y = screenCenterY;
    const angle = Math.atan2(y - screenCenterY, distanceFromBarrier + radius * 0.1);
    
    // Interference phase: φ = (2πd sin(θ))/λ
    const phase = (2 * Math.PI * d * Math.sin(angle)) / wavelength;
    const intensity = Math.cos(phase) ** 2; // cos² gives interference pattern
    
    // Map intensity to pattern height (bright = high, dark = low)
    const patternHeight = intensity * radius * 0.6;
    const patternY = screenCenterY - patternHeight / 2;
    
    if (x === screenStartX) {
      ctx.moveTo(x, patternY);
    } else {
      ctx.lineTo(x, patternY);
    }
  }
  ctx.stroke();
  
  // Draw interference fringes (bright and dark bands) more accurately
  // These represent the actual pattern seen on the detection screen
  ctx.strokeStyle = 'rgba(99, 102, 241, 0.5)';
  ctx.lineWidth = getLineWidth(radius, 0.008);
  const fringeCount = 7; // Number of interference fringes (bright/dark pairs)
  const screenHeight = radius * 0.6;
  
  for (let i = -fringeCount; i <= fringeCount; i++) {
    // Calculate fringe position based on interference formula
    // Bright fringes occur at: d sin(θ) = mλ (m = 0, ±1, ±2, ...)
    const fringeIndex = i;
    const fringeY = screenCenterY + (fringeIndex / fringeCount) * screenHeight;
    
    // Draw horizontal line representing the fringe
    ctx.beginPath();
    ctx.moveTo(screenStartX, fringeY);
    ctx.lineTo(screenEndX, fringeY);
    ctx.stroke();
    
    // Make central and even-numbered fringes brighter (constructive interference)
    if (Math.abs(fringeIndex) % 2 === 0) {
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.8)';
      ctx.lineWidth = getLineWidth(radius, 0.012);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.5)';
      ctx.lineWidth = getLineWidth(radius, 0.008);
    }
  }
  
  // Label
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#6366f1';
  ctx.fillText('Double-Slit: Wave Interference', centerX, centerY + radius * 0.9);
  ctx.fillText('(Unobserved = Wave Behavior)', centerX, centerY + radius * 0.9 + fontSize * 1.2);
}

/**
 * 31. Zen Buddhism: Enso circle
 */
function drawEnso(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw incomplete circle (enso) - represents enlightenment, the universe, void
  const ensoRadius = radius * 0.7;
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.03);
  ctx.lineCap = 'round';
  
  // Draw circle with opening (traditional enso)
  ctx.beginPath();
  ctx.arc(centerX, centerY, ensoRadius, Math.PI * 0.2, Math.PI * 2 - Math.PI * 0.2, false);
  ctx.stroke();
  
  // Add brush stroke effect (thicker at start, thinner at end)
  ctx.beginPath();
  ctx.arc(centerX, centerY, ensoRadius, Math.PI * 0.2, Math.PI * 0.4, false);
  ctx.lineWidth = getLineWidth(radius, 0.05);
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Enso - The Circle', centerX, centerY + radius * 0.9);
}

/**
 * 33. Alchemy: Transformation symbol
 */
function drawAlchemy(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw alchemical symbol (simplified - triangle with cross)
  // Triangle (fire/transformation)
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  ctx.beginPath();
  ctx.moveTo(centerX, centerY - radius * 0.6);
  ctx.lineTo(centerX - radius * 0.5, centerY + radius * 0.3);
  ctx.lineTo(centerX + radius * 0.5, centerY + radius * 0.3);
  ctx.closePath();
  ctx.stroke();
  
  // Cross (union of opposites)
  ctx.beginPath();
  ctx.moveTo(centerX, centerY - radius * 0.2);
  ctx.lineTo(centerX, centerY + radius * 0.2);
  ctx.moveTo(centerX - radius * 0.2, centerY);
  ctx.lineTo(centerX + radius * 0.2, centerY);
  ctx.stroke();
  
  // Circle (wholeness)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.3, 0, Math.PI * 2);
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Transformation', centerX, centerY + radius * 0.9);
}

/**
 * 35. Mayan Calendar: Calendar cycles
 */
function drawMayanCalendar(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw concentric circles representing different cycles
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.012);
  
  // Outer circle (Long Count)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.9, 0, Math.PI * 2);
  ctx.stroke();
  
  // Middle circle (Haab - 365 days)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.6, 0, Math.PI * 2);
  ctx.stroke();
  
  // Inner circle (Tzolk\'in - 260 days)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.3, 0, Math.PI * 2);
  ctx.stroke();
  
  // Draw 20 day glyphs around inner circle
  for (let i = 0; i < 20; i++) {
    const angle = (i * Math.PI * 2) / 20;
    const x = centerX + radius * 0.45 * Math.cos(angle);
    const y = centerY + radius * 0.45 * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.03, 0, Math.PI * 2);
    ctx.fill();
  }
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Tzolk\'in: 260 days', centerX, centerY + radius * 0.9);
}

/**
 * 39. Sacred Numbers: Number relationships
 */
function drawSacredNumbers(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = getFontSize(radius, 0.14);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  
  // Display key sacred numbers
  const numbers = [
    { num: '108', desc: 'Mala beads' },
    { num: '144', desc: '12×12' },
    { num: '360', desc: 'Circle' },
    { num: '432', desc: 'Hz tuning' }
  ];
  
  const lineHeight = fontSize * 1.4;
  numbers.forEach((item, i) => {
    const y = centerY - radius * 0.6 + i * lineHeight;
    ctx.fillText(`${item.num} - ${item.desc}`, centerX, y);
  });
  
  // Draw circular arrangement
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.01);
  const circleRadius = radius * 0.08;
  for (let i = 0; i < 8; i++) {
    const angle = (i * Math.PI * 2) / 8;
    const x = centerX + radius * 0.7 * Math.cos(angle);
    const y = centerY + radius * 0.7 * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(x, y, circleRadius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.font = `${fontSize * 0.7}px monospace`;
    ctx.fillText([108, 144, 360, 432, 72, 1080, 432000, 144000][i]?.toString() || '', x, y + fontSize * 0.25);
  }
}

/**
 * 40. Music Theory: Harmonic series
 */
function drawHarmonicSeries(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw harmonic series visualization
  const fundamental = radius * 0.1;
  const harmonics = [1, 2, 3, 4, 5]; // First 5 harmonics
  
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  
  harmonics.forEach((harmonic, i) => {
    const freq = fundamental * harmonic;
    const y = centerY - radius * 0.6 + i * radius * 0.25;
    const waveWidth = radius * 1.4;
    const waveAmplitude = radius * 0.08 / harmonic; // Higher harmonics have less amplitude
    
    ctx.beginPath();
    for (let x = centerX - waveWidth / 2; x <= centerX + waveWidth / 2; x += 2) {
      const t = (x - (centerX - waveWidth / 2)) / waveWidth;
      const waveY = y + Math.sin(t * freq * Math.PI * 2) * waveAmplitude;
      if (x === centerX - waveWidth / 2) ctx.moveTo(x, waveY);
      else ctx.lineTo(x, waveY);
    }
    ctx.stroke();
    
    // Label
    const fontSize = getFontSize(radius, 0.08);
    ctx.font = `${fontSize}px monospace`;
    ctx.fillText(`${harmonic}:1 ratio`, centerX + waveWidth / 2 + fontSize, y);
  });
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Harmonic Series', centerX, centerY + radius * 0.9);
}

/**
 * 42. Four Noble Truths: Four-part structure
 */
function drawFourNobleTruths(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw four quadrants
  const truths = ['Dukkha', 'Samudaya', 'Nirodha', 'Magga'];
  const colors = ['#dc2626', '#f97316', '#eab308', '#16a34a'];
  
  ctx.lineWidth = getLineWidth(radius, 0.015);
  
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI * 2) / 4 - Math.PI / 4;
    const x = centerX + radius * 0.5 * Math.cos(angle);
    const y = centerY + radius * 0.5 * Math.sin(angle);
    
    // Draw quadrant
    ctx.strokeStyle = colors[i];
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(x, y);
    ctx.stroke();
    
    // Draw circle for truth
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.15, 0, Math.PI * 2);
    ctx.stroke();
    
    // Label
    const fontSize = getFontSize(radius, 0.1);
    ctx.font = `${fontSize}px sans-serif`;
    ctx.fillText(truths[i], x, y + radius * 0.3);
  }
  
  // Center circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.1, 0, Math.PI * 2);
  ctx.stroke();
}

/**
 * 45. Enneagram: Nine-pointed symbol
 */
function drawEnneagram(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw enneagram: 9 points on a circle, connected in specific pattern
  const points: { x: number; y: number }[] = [];
  const pointRadius = radius * 0.8;
  
  // Calculate 9 points
  for (let i = 0; i < 9; i++) {
    const angle = (i * Math.PI * 2) / 9 - Math.PI / 2;
    points.push({
      x: centerX + pointRadius * Math.cos(angle),
      y: centerY + pointRadius * Math.sin(angle)
    });
  }
  
  // Draw connecting lines (enneagram pattern: 1-4-2-8-5-7-1 and 3-6-9-3)
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.012);
  
  // First triangle: 3-6-9
  ctx.beginPath();
  ctx.moveTo(points[2].x, points[2].y);
  ctx.lineTo(points[5].x, points[5].y);
  ctx.lineTo(points[8].x, points[8].y);
  ctx.closePath();
  ctx.stroke();
  
  // Hexagon: 1-4-2-8-5-7
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  ctx.lineTo(points[3].x, points[3].y);
  ctx.lineTo(points[1].x, points[1].y);
  ctx.lineTo(points[7].x, points[7].y);
  ctx.lineTo(points[4].x, points[4].y);
  ctx.lineTo(points[6].x, points[6].y);
  ctx.closePath();
  ctx.stroke();
  
  // Draw points
  ctx.fillStyle = '#6366f1';
  points.forEach((point, i) => {
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius * 0.04, 0, Math.PI * 2);
    ctx.fill();
    
    // Number label
    const fontSize = getFontSize(radius, 0.08);
    ctx.font = `${fontSize}px monospace`;
    ctx.fillStyle = '#6366f1';
    ctx.textAlign = 'center';
    ctx.fillText((i + 1).toString(), point.x, point.y + fontSize * 0.35);
  });
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('9 Types', centerX, centerY + radius * 0.9);
}

/**
 * 32. Stoicism: Dichotomy of Control
 */
function drawStoicism(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw circle divided into two parts: what we control vs what we don't
  const circleRadius = radius * 0.7;
  
  // Outer circle
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  ctx.beginPath();
  ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
  ctx.stroke();
  
  // Divide circle (vertical line)
  ctx.beginPath();
  ctx.moveTo(centerX, centerY - circleRadius);
  ctx.lineTo(centerX, centerY + circleRadius);
  ctx.stroke();
  
  // Left side: What we control (smaller)
  ctx.fillStyle = 'rgba(99, 102, 241, 0.2)';
  ctx.beginPath();
  ctx.arc(centerX, centerY, circleRadius, Math.PI / 2, Math.PI * 3 / 2, false);
  ctx.lineTo(centerX, centerY);
  ctx.closePath();
  ctx.fill();
  
  // Right side: What we don't control (larger)
  ctx.fillStyle = 'rgba(100, 116, 139, 0.1)';
  ctx.beginPath();
  ctx.arc(centerX, centerY, circleRadius, Math.PI * 3 / 2, Math.PI / 2, false);
  ctx.lineTo(centerX, centerY);
  ctx.closePath();
  ctx.fill();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#6366f1';
  ctx.fillText('Control', centerX - radius * 0.3, centerY);
  ctx.fillText('No Control', centerX + radius * 0.3, centerY);
}

/**
 * 34. Sacred Architecture: Geometric proportions
 */
function drawSacredArchitecture(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw pyramid with golden ratio proportions
  const phi = (1 + Math.sqrt(5)) / 2;
  const baseWidth = radius * 1.2;
  const height = baseWidth / phi; // Golden ratio
  
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  
  // Draw pyramid
  ctx.beginPath();
  ctx.moveTo(centerX, centerY - height / 2);
  ctx.lineTo(centerX - baseWidth / 2, centerY + height / 2);
  ctx.lineTo(centerX + baseWidth / 2, centerY + height / 2);
  ctx.closePath();
  ctx.stroke();
  
  // Draw base square
  ctx.strokeRect(centerX - baseWidth / 2, centerY + height / 2, baseWidth, baseWidth / phi);
  
  // Show golden ratio
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('φ = 1.618', centerX, centerY + radius * 0.9);
}

/**
 * 36. Advaita Vedanta: Non-dual symbol
 */
function drawAdvaitaVedanta(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw symbol representing non-duality: one circle (Brahman/Atman)
  const circleRadius = radius * 0.8;
  
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  ctx.beginPath();
  ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
  ctx.stroke();
  
  // Inner point (Bindu - the One)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.03, 0, Math.PI * 2);
  ctx.fill();
  
  // Text: "I am That" (Tat Tvam Asi)
  const fontSize = getFontSize(radius, 0.12);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Tat Tvam Asi', centerX, centerY);
  ctx.fillText('(I am That)', centerX, centerY + fontSize * 1.3);
  
  ctx.font = `${getFontSize(radius, 0.1)}px sans-serif`;
  ctx.fillText('Non-Dual Reality', centerX, centerY + radius * 0.9);
}

/**
 * 37. Lucid Dreaming: Dream state visualization
 */
function drawLucidDreaming(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw nested circles representing dream layers
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.012);
  
  // Outer: Waking state
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.9, 0, Math.PI * 2);
  ctx.stroke();
  
  // Middle: Dream state
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.6, 0, Math.PI * 2);
  ctx.stroke();
  
  // Inner: Lucid awareness
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.3, 0, Math.PI * 2);
  ctx.stroke();
  
  // Draw awareness point in center
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.05, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw wavy lines representing dream content
  ctx.strokeStyle = '#6366f180';
  for (let i = 0; i < 8; i++) {
    const angle = (i * Math.PI * 2) / 8;
    const startX = centerX + radius * 0.3 * Math.cos(angle);
    const startY = centerY + radius * 0.3 * Math.sin(angle);
    const endX = centerX + radius * 0.6 * Math.cos(angle);
    const endY = centerY + radius * 0.6 * Math.sin(angle);
    
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    // Wavy line
    for (let t = 0; t <= 1; t += 0.1) {
      const x = startX + (endX - startX) * t;
      const y = startY + (endY - startY) * t + Math.sin(t * Math.PI * 4) * radius * 0.05;
      if (t === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#6366f1';
  ctx.fillText('Lucid Awareness', centerX, centerY + radius * 0.9);
}

/**
 * 38. Shadow Work: Integration symbol
 */
function drawShadowWork(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw two circles merging: conscious and shadow
  const circleRadius = radius * 0.5;
  
  // Left circle: Conscious (light)
  ctx.strokeStyle = '#6366f1';
  ctx.fillStyle = 'rgba(99, 102, 241, 0.2)';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  ctx.beginPath();
  ctx.arc(centerX - radius * 0.2, centerY, circleRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  
  // Right circle: Shadow (darker)
  ctx.strokeStyle = '#8b5cf6';
  ctx.fillStyle = 'rgba(139, 92, 246, 0.3)';
  ctx.beginPath();
  ctx.arc(centerX + radius * 0.2, centerY, circleRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  
  // Overlap area: Integration
  ctx.fillStyle = 'rgba(99, 102, 241, 0.4)';
  ctx.beginPath();
  ctx.arc(centerX - radius * 0.2, centerY, circleRadius, -Math.PI / 3, Math.PI / 3, false);
  ctx.arc(centerX + radius * 0.2, centerY, circleRadius, Math.PI * 2 / 3, Math.PI * 4 / 3, false);
  ctx.closePath();
  ctx.fill();
  
  // Center: Wholeness
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#6366f1';
  ctx.fillText('Integration', centerX, centerY + radius * 0.9);
}

/**
 * 41. Bhagavad Gita: Three paths symbol
 */
function drawBhagavadGita(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw three paths converging: Karma, Bhakti, Jnana Yoga
  const pathLength = radius * 0.7;
  
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  
  // Three paths from different directions
  const paths = [
    { angle: -Math.PI / 2, label: 'Karma' }, // Top
    { angle: Math.PI / 6, label: 'Bhakti' }, // Right
    { angle: Math.PI * 5 / 6, label: 'Jnana' } // Left
  ];
  
  paths.forEach((path, i) => {
    const startX = centerX + pathLength * Math.cos(path.angle);
    const startY = centerY + pathLength * Math.sin(path.angle);
    
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(centerX, centerY);
    ctx.stroke();
    
    // Label
    const fontSize = getFontSize(radius, 0.1);
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    const labelX = centerX + (pathLength + radius * 0.2) * Math.cos(path.angle);
    const labelY = centerY + (pathLength + radius * 0.2) * Math.sin(path.angle);
    ctx.fillText(path.label, labelX, labelY);
  });
  
  // Center: Liberation
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Moksha', centerX, centerY + radius * 0.9);
}

/**
 * 43. Hermetic Principles: Seven principles
 */
function drawHermeticPrinciples(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw seven-pointed star or seven principles arranged
  const principles = [
    'Mentalism', 'Correspondence', 'Vibration',
    'Polarity', 'Rhythm', 'Cause & Effect', 'Gender'
  ];
  
  // Draw seven points in circle
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < 7; i++) {
    const angle = (i * Math.PI * 2) / 7 - Math.PI / 2;
    points.push({
      x: centerX + radius * 0.6 * Math.cos(angle),
      y: centerY + radius * 0.6 * Math.sin(angle)
    });
  }
  
  // Draw connecting lines (heptagram pattern)
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.012);
  
  // Connect every 3rd point (heptagram)
  for (let i = 0; i < 7; i++) {
    const next = (i + 3) % 7;
    ctx.beginPath();
    ctx.moveTo(points[i].x, points[i].y);
    ctx.lineTo(points[next].x, points[next].y);
    ctx.stroke();
  }
  
  // Draw points
  ctx.fillStyle = '#6366f1';
  points.forEach((point) => {
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius * 0.04, 0, Math.PI * 2);
    ctx.fill();
  });
  
  // Center circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.1, 0, Math.PI * 2);
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('7 Principles', centerX, centerY + radius * 0.9);
}

/**
 * 44. Indigenous Wisdom: Medicine wheel or interconnected circles
 */
function drawIndigenousWisdom(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Draw medicine wheel: four directions, four elements
  const directions = [
    { name: 'East', color: '#eab308', angle: 0 },
    { name: 'South', color: '#dc2626', angle: Math.PI / 2 },
    { name: 'West', color: '#1e40af', angle: Math.PI },
    { name: 'North', color: '#16a34a', angle: Math.PI * 3 / 2 }
  ];
  
  // Outer circle
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.8, 0, Math.PI * 2);
  ctx.stroke();
  
  // Draw four quadrants
  directions.forEach((dir, i) => {
    const angle = dir.angle;
    const x = centerX + radius * 0.5 * Math.cos(angle);
    const y = centerY + radius * 0.5 * Math.sin(angle);
    
    // Draw line to center
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(x, y);
    ctx.stroke();
    
    // Draw circle at direction
    ctx.strokeStyle = dir.color;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.15, 0, Math.PI * 2);
    ctx.stroke();
  });
  
  // Center circle (all relations)
  ctx.strokeStyle = '#6366f1';
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('All My Relations', centerX, centerY + radius * 0.9);
}

/**
 * 46. Platonic Space: The Receptacle (Khôra) and Three Realms
 * 
 * Visualizes Plato's three realms:
 * - Top: Intelligible Realm of Being (Forms)
 * - Middle: The Receptacle (Khôra) - Space/Medium
 * - Bottom: Sensible Realm of Becoming (Physical World)
 * 
 * Shows Platonic solids within the Receptacle, representing
 * how perfect Forms manifest through space into physical reality.
 */
function drawPlatonicSpace(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  const fontSize = getFontSize(radius, 0.1);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#6366f1';
  
  // Three realms stacked vertically
  const realmHeight = radius * 0.25;
  const topY = centerY - radius * 0.5;
  const middleY = centerY;
  const bottomY = centerY + radius * 0.5;
  
  // Top: Intelligible Realm (Being - Forms)
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  ctx.fillStyle = 'rgba(99, 102, 241, 0.1)';
  ctx.fillRect(centerX - radius * 0.7, topY - realmHeight / 2, radius * 1.4, realmHeight);
  ctx.strokeRect(centerX - radius * 0.7, topY - realmHeight / 2, radius * 1.4, realmHeight);
  
  // Draw geometric forms (representing perfect Forms)
  const formSize = radius * 0.08;
  const forms = [
    { x: centerX - radius * 0.4, y: topY, shape: 'circle' },
    { x: centerX, y: topY, shape: 'triangle' },
    { x: centerX + radius * 0.4, y: topY, shape: 'square' }
  ];
  
  forms.forEach(form => {
    ctx.strokeStyle = '#6366f1';
    ctx.beginPath();
    if (form.shape === 'circle') {
      ctx.arc(form.x, form.y, formSize, 0, Math.PI * 2);
    } else if (form.shape === 'triangle') {
      ctx.moveTo(form.x, form.y - formSize);
      ctx.lineTo(form.x - formSize, form.y + formSize);
      ctx.lineTo(form.x + formSize, form.y + formSize);
      ctx.closePath();
    } else if (form.shape === 'square') {
      ctx.rect(form.x - formSize, form.y - formSize, formSize * 2, formSize * 2);
    }
    ctx.stroke();
  });
  
  ctx.fillText('Being (Forms)', centerX, topY - realmHeight / 2 - fontSize * 0.5);
  
  // Middle: The Receptacle (Khôra) - Space
  ctx.strokeStyle = '#8b5cf6';
  ctx.lineWidth = getLineWidth(radius, 0.02);
  ctx.fillStyle = 'rgba(139, 92, 246, 0.15)';
  ctx.fillRect(centerX - radius * 0.7, middleY - realmHeight / 2, radius * 1.4, realmHeight);
  ctx.strokeRect(centerX - radius * 0.7, middleY - realmHeight / 2, radius * 1.4, realmHeight);
  
  // Draw Platonic solids within the Receptacle (simplified 2D projections)
  // Tetrahedron (top-left), Cube (top-right), Octahedron (bottom-left), Dodecahedron (bottom-right)
  const solidSize = radius * 0.1;
  
  // Tetrahedron (triangle - Fire)
  ctx.strokeStyle = '#ef4444';
  ctx.beginPath();
  ctx.moveTo(centerX - radius * 0.3, middleY - solidSize);
  ctx.lineTo(centerX - radius * 0.3 - solidSize, middleY + solidSize);
  ctx.lineTo(centerX - radius * 0.3 + solidSize, middleY + solidSize);
  ctx.closePath();
  ctx.stroke();
  
  // Cube (square - Earth)
  ctx.strokeStyle = '#f97316';
  ctx.strokeRect(centerX + radius * 0.1, middleY - solidSize, solidSize * 2, solidSize * 2);
  
  // Octahedron (diamond - Air)
  ctx.strokeStyle = '#3b82f6';
  ctx.beginPath();
  ctx.moveTo(centerX - radius * 0.1, middleY - solidSize);
  ctx.lineTo(centerX - radius * 0.1 - solidSize, middleY);
  ctx.lineTo(centerX - radius * 0.1, middleY + solidSize);
  ctx.lineTo(centerX - radius * 0.1 + solidSize, middleY);
  ctx.closePath();
  ctx.stroke();
  
  // Icosahedron (simplified - Water)
  ctx.strokeStyle = '#06b6d4';
  ctx.beginPath();
  ctx.arc(centerX + radius * 0.3, middleY, solidSize, 0, Math.PI * 2);
  ctx.stroke();
  
  ctx.fillStyle = '#8b5cf6';
  ctx.fillText('Receptacle (Khôra)', centerX, middleY - realmHeight / 2 - fontSize * 0.5);
  ctx.fillText('Space/Medium', centerX, middleY + realmHeight / 2 + fontSize * 0.8);
  
  // Bottom: Sensible Realm (Becoming - Physical)
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.015);
  ctx.fillStyle = 'rgba(99, 102, 241, 0.05)';
  ctx.fillRect(centerX - radius * 0.7, bottomY - realmHeight / 2, radius * 1.4, realmHeight);
  ctx.strokeRect(centerX - radius * 0.7, bottomY - realmHeight / 2, radius * 1.4, realmHeight);
  
  // Draw imperfect physical manifestations (irregular shapes)
  // Using deterministic variations to represent imperfection
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = getLineWidth(radius, 0.01);
  const variations = [0.1, -0.15, 0.2, -0.1, 0.15]; // Deterministic offsets
  const sizeVariations = [0.8, 1.1, 0.9, 1.2, 0.7]; // Deterministic size variations
  
  for (let i = 0; i < 5; i++) {
    const x = centerX - radius * 0.5 + (i * radius * 0.25);
    const y = bottomY + variations[i] * realmHeight * 0.3;
    const size = solidSize * sizeVariations[i];
    
    // Draw irregular shape
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.stroke();
  }
  
  ctx.fillStyle = '#6366f1';
  ctx.fillText('Becoming (Physical)', centerX, bottomY + realmHeight / 2 + fontSize * 0.8);
  
  // Draw arrows showing manifestation flow
  ctx.strokeStyle = 'rgba(99, 102, 241, 0.4)';
  ctx.lineWidth = getLineWidth(radius, 0.01);
  ctx.fillStyle = 'rgba(99, 102, 241, 0.4)';
  
  // From Being to Receptacle
  for (let i = 0; i < 3; i++) {
    const x = centerX - radius * 0.3 + i * radius * 0.3;
    drawArrow(ctx, x, topY + realmHeight / 2, x, middleY - realmHeight / 2);
  }
  
  // From Receptacle to Becoming
  for (let i = 0; i < 3; i++) {
    const x = centerX - radius * 0.3 + i * radius * 0.3;
    drawArrow(ctx, x, middleY + realmHeight / 2, x, bottomY - realmHeight / 2);
  }
  
  // Label with Greek letter
  ctx.font = `${getFontSize(radius, 0.12)}px sans-serif`;
  ctx.fillText('Χώρα (Khôra)', centerX, centerY + radius * 0.85);
}

/**
 * Helper function to draw an arrow
 */
function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const arrowLength = 8;
  const arrowAngle = Math.atan2(y2 - y1, x2 - x1);
  
  // Draw line
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  
  // Draw arrowhead
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - arrowLength * Math.cos(arrowAngle - Math.PI / 6),
    y2 - arrowLength * Math.sin(arrowAngle - Math.PI / 6)
  );
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - arrowLength * Math.cos(arrowAngle + Math.PI / 6),
    y2 - arrowLength * Math.sin(arrowAngle + Math.PI / 6)
  );
  ctx.stroke();
}

/**
 * Default: Simple circle
 */
function drawDefault(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number
) {
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.stroke();
}

export default WisdomMathFigure;

